"use client";

import dynamic from "next/dynamic";
import NextLink from "next/link";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../../../helpers/classNames";
import { debug } from "../../../../helpers/debug";
import { getWindow } from "../../../../helpers/getWindow";
import { isSnowSeason } from "../../../../helpers/season";
import { getRoute } from "../../../../helpers/urls/getRoute";
import { useCountry } from "../../../../hooks/useCountry";
import { usePageParams } from "../../../../hooks/usePageParams";
import { isSpecialCam } from "../../../../network/matlocq/helpers/livecams";
import { useLivecam } from "../../../../network/matlocq/queries/Livecam";
import { useSkiArea } from "../../../../network/matlocq/queries/SkiArea";
import { useNativeToolbar, useStickyAdOffsets } from "../../../Ad/hooks";
import { AdSlot } from "../../../Ad/slots";
import { AutoSuggest } from "../../../AutoSuggest";
import { Button } from "../../../Button";
import { useConsent, useIsPay } from "../../../Consent/hooks";
import { useDayjs } from "../../../Dayjs/hooks";
import { ErrorBoundary } from "../../../ErrorBoundary";
import { Menu } from "../../../Menu/mobile";
import { useNotificationOverlay } from "../../../Notifications/hooks";
import { WebsiteLogo } from "../../../WebsiteLogo";
import { useHeaderScroll } from "../../hooks";
import { Magnifier } from "../magnifier";
import styles from "./index.module.scss";

const Overlay = dynamic(
    () => import("../../../Notifications/Overlay").then((mod) => mod.Overlay),
    { ssr: false }
);

const Favorites = dynamic(
    () => import("../../../Favorites/mobile").then((mod) => mod.Favorites),
    { ssr: false }
);

export const Header: React.FC = () => {
    const window = getWindow();
    const country = useCountry();
    const dayjs = useDayjs();
    const pageParams = usePageParams();
    const { pageType } = pageParams;
    const { data: livecam } = useLivecam(
        pageType?.isLivecamDetailPage && typeof pageParams.id === "string"
            ? pageParams.id
            : null
    );
    const { data: skiArea } = useSkiArea({
        id: typeof pageParams.id === "string" ? pageParams.id : null,
        enabled: pageType?.isAnySkiAreaDetailPage,
    });
    const consent = useConsent();
    const isPay = useIsPay();
    const { isVisible, toggleOverlay } = useNotificationOverlay();
    const innerTopRef = useRef<HTMLDivElement>(null);
    const { top } = useStickyAdOffsets();
    const [isShowroom, setIsShowroom] = useState(false);
    const query = useSearchParams();
    useHeaderScroll({ ref: innerTopRef, styles });
    const nativeToolbar = useNativeToolbar();

    useEffect(() => {
        if (
            (window &&
                window.sessionStorage.getItem("wcom_cp_showroom") === "true") ||
            query.get("cpShowroom") === "true"
        ) {
            requestAnimationFrame(() => setIsShowroom(true));
            // eslint-disable-next-line testing-library/no-debugging-utils
            debug(
                "[CP] Programmatic showroom is active. Close tab or clear session storage to reset."
            );
        }
    }, [window, query]);

    const hideToolbar =
        pageType?.isAnyLivecamPage || skiArea?.sponsoring.isSponsored || isPay;
    const hideAds =
        (pageType?.isLivecamDetailPage &&
            (country !== "de" || (livecam && isSpecialCam(livecam)))) ||
        skiArea?.sponsoring.isSponsored ||
        isPay;
    const hasPushSupport = window && "PushManager" in window;

    return (
        <header>
            <div
                className={styles.outer}
                style={{
                    paddingTop: country === "at" || hideAds ? 0 : top,
                }}
            >
                {/* reserve height to have always enough space for the header */}
                <div className={styles.innerTopSpacer} />
                <div className={styles.innerTop} ref={innerTopRef}>
                    <WebsiteLogo />
                    <div className={styles.innerRight}>
                        {isShowroom ? (
                            <span
                                className={cn(
                                    styles.programmaticStatus,
                                    "icon-house-cash"
                                )}
                                title="Programmatic showroom is active. Close tab or clear session storage to reset."
                            />
                        ) : null}
                        <Magnifier />
                        {consent && !isPay && hasPushSupport ? (
                            <ErrorBoundary>
                                <Button
                                    variant="unstyled"
                                    className={styles.wrapperBell}
                                    onClick={toggleOverlay}
                                    id="notification-bell"
                                    aria-label="Benachrichtigungseinstellungen öffnen"
                                >
                                    <span className="icon-bell" />
                                    <span
                                        className={cn(
                                            styles.bellDot,
                                            "bell-dot"
                                        )}
                                    />
                                </Button>
                                {isVisible ? (
                                    <Overlay toggleOverlay={toggleOverlay} />
                                ) : null}
                            </ErrorBoundary>
                        ) : null}
                        {isSnowSeason(dayjs) ? (
                            <NextLink
                                className={styles.wrapperOutline}
                                href={getRoute({
                                    parameters: {
                                        pageName: "skiAreaList",
                                    },
                                    country,
                                })}
                                target="_self"
                                aria-label="Skigebiete öffnen"
                                data-label="Skigebiete öffnen"
                                title="Alle Infos zu Wetter, Neuschnee, Pisten, Lifte sowie Skipasspreise - Wetter ist wetter.com"
                            >
                                <span className="snow-icon-skiarea" />
                            </NextLink>
                        ) : (
                            <a
                                className={styles.wrapperOutline}
                                href={getRoute({
                                    parameters: {
                                        pageName: "healthPollenLandingpage",
                                    },
                                    country,
                                })}
                                target="_self"
                                aria-label="Pollenflug"
                                data-label="Pollenflug"
                                title="Infos zum Pollenflug in Deiner Region"
                            >
                                <span className="icon-polle" />
                            </a>
                        )}
                        <ErrorBoundary>
                            <Menu />
                        </ErrorBoundary>
                    </div>
                </div>
                <div
                    className={
                        pageType?.isAnySkiAreasPage
                            ? styles.innerBottomSkiArea
                            : styles.innerBottom
                    }
                >
                    {hideToolbar ? null : (
                        <AdSlot
                            slot={{
                                de: "toolbar",
                                at: "toolbar",
                                ch: "toolbar",
                                pl: undefined,
                            }}
                            minHeight={nativeToolbar ? "72px" : "0"}
                        />
                    )}
                    {!pageType?.isPlainPage && (
                        <>
                            <ErrorBoundary>
                                <AutoSuggest />
                            </ErrorBoundary>
                            {pageType?.isHomePage ? (
                                <div className={styles.wrapperFavorites}>
                                    <ErrorBoundary>
                                        <Favorites />
                                    </ErrorBoundary>
                                </div>
                            ) : null}
                        </>
                    )}
                    {hideAds ? null : (
                        <AdSlot
                            slot={{
                                de: undefined,
                                at: "mbanner1",
                                ch: undefined,
                                pl: undefined,
                            }}
                            minHeight="170px"
                        />
                    )}
                </div>
            </div>
        </header>
    );
};
